import Api from "@/api.js";

/**
 * WebRTC Mixin
 *
 * Attempts to create WebRTC connection to expose if user is behind proxy
 * and writes results to the "result" object.
 */
export const webRTCMixin = {

    data() {
        return {
            webRTCTimeoutMs: 3500,
            iceGatheringComplete: false,
            rtcStartTime: null,
            result: {
                ping_client: null,
                rtc_timeout: false,
                rtc_time: null,
                rtc_enabled: true,
                rtc_error: null,
                rtc_error_code: null,
                rtc_ips: [],
            },
            iceServers: [
                {urls: "stun:stun.l.google.com:19302"},
                {urls: "stun:stun1.l.google.com:19302"},
                {urls: "stun:stun2.l.google.com:19302"},
                {urls: "stun:stun3.l.google.com:19302"},
                {urls: "stun:stun4.l.google.com:19302"},
            ],
        }
    },

    methods: {

        runWebRTCTest() {

            return new Promise((resolve, reject) => {

                this.findIP()
                    .then(() => {
                        this.ping()
                            .then(() => {
                                resolve();
                            })
                            .catch((err) => {
                                console.error(err);
                                resolve();
                            })
                    })
                    .catch((err) => {
                        console.error(err);
                        this.errorMessage = 'Unexpected error. Please contact support for assistance!'
                        this.loading = false;
                        this.sendValidationRequest();
                    })
            });
        },

        /**
         * Create WebRTC connection that leaks users real IP
         * when behind proxy or VPN.
         */
        findIP() {

            return new Promise((resolve, reject) => {

                this.rtcStartTime = performance.now();

                let myPeerConnection = window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection;

                if (!myPeerConnection) {
                    this.rtc_enabled = false;
                    reject();
                }

                let pc = new myPeerConnection({iceServers: this.iceServers});
                let ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/g;

                pc.createDataChannel("");

                pc.createOffer()
                    .then((offer) => {

                        offer.sdp.split('\n').forEach((line) => {
                            if (line.indexOf('candidate') < 0) return;
                            line.match(ipRegex).forEach(this.recordIp);
                        });

                        pc.setLocalDescription(offer);
                    })
                    .catch((error) => {

                        if (error) {
                            this.result.rtc_error = "Create offer returned error " + error;
                        } else {
                            this.result.rtc_error = "Unknown error";
                            console.error(error);
                        }
                        this.recordRtcTime();
                        reject();
                    });

                pc.addEventListener("icecandidateerror", (event) => {
                    this.recordRtcTime();
                    this.result.rtc_error = event.errorCode + " " + event.errorText;
                    this.result.rtc_error_code = event.errorCode;
                });

                pc.addEventListener("icegatheringstatechange", (ev) => {

                    switch (pc.iceGatheringState) {

                        case "complete":

                            // Clear candidate if IPs were found. Chrome incorrectly throws
                            // candidate error
                            if (this.result.rtc_ips.length > 0) {
                                this.result.rtc_error = null;
                                this.result.rtc_error_code = null;
                            }

                            this.iceGatheringComplete = true;
                            this.recordRtcTime();
                            resolve();
                    }
                });

                pc.onicecandidate = (ice) => {
                    if (ice && ice.candidate && ice.candidate.candidate && ice.candidate.candidate.match(ipRegex)) {
                        ice.candidate.candidate.match(ipRegex).forEach(this.recordIp);
                    }
                };

                setTimeout(() => {
                    this.result.rtc_timeout = true;
                    this.recordRtcTime();
                    resolve();
                }, this.webRTCTimeoutMs);
            });
        },

        recordRtcTime() {
            this.result.rtc_time = (performance.now() - this.rtcStartTime).toFixed(2);
        },

        recordIp(ip) {

            if (ip === '0.0.0.0') return;

            if (!this.result.rtc_ips.includes(ip)) {
                this.result.rtc_ips.push(ip);
            }
        },

        /**
         * Attempt to fetch image that doesn't exist to measure ping time
         * Because HTTP response is two way connection we divide the results
         * by 2 to get as close to real ping time which is one way.
         */
        ping() {

            return new Promise((resolve) => {

                let url = window.location.origin + "/img/dot.png";
                let started = performance.now();
                let cacheBuster = '?key=' + started;

                url += cacheBuster;

                Api.get(url)
                    .catch(() => {
                        this.result.ping_client = ((performance.now() - started) / 2).toFixed(2);
                        resolve();
                    });
            });
        }
    }
};

